<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            rowClass="text-center"
            :perPage="itemsPerPage"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            :showColumns="false"
            @page-changed="handlePageChange"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-if="searchView" v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Export Button -->
                    <ExportSelect
                        v-if="exportView"
                        :label="$t('treatment_customer.export')"
                        :options="[
                            { label: $t('treatment_customer.csv'), value: 'csv' },
                            { label: $t('treatment_customer.excel'), value: 'excel' },
                        ]"
                        :disabled="exportLoading"
                        :loading="exportLoading"
                        @export-selected="downloadOrders"
                    />

                    <div class="ppv2-flex-1"></div>
                </div>
            </template>

            <!-- Column: ID -->
            <template #column-id="{ item }">
                <a
                    :href="`/back-office/commandes/${item.id}/descriptif`"
                    class="ppv2-text-black poppinsRegular ppv2-underline"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    #{{ item.id }}
                </a>
            </template>

            <!-- Column: Date -->
            <template #column-start="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.type_order === 'booking' ? formatDate(item.start) : item.created_at }}
                </span>
            </template>

            <!-- Column: Items -->
            <template #column-prestations="{ item }">
                <!-- BOOKING -->
                <div v-if="item.prestations && item.prestations.length > 0">
                    <div class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-70">
                        {{
                            item.prestations
                                .slice(0, 2)
                                .map((i) => i || $t('invoices.article'))
                                .join(', ')
                        }}
                        <span v-if="item.prestations.length > 2">...</span>
                    </div>
                </div>
                <!-- DEFAULT -->
                <span v-else class="ppv2-text-black ppv2-text-opacity-70"> - </span>
            </template>

            <template #column-nbre="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.nbre }}
                </span>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <span :class="getStatus(item)">
                    {{ $t(`customerInfo.status.${item.status.key}`) }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div v-if="item.type_order === 'booking'" class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier (Link to V2 Edit Page) -->
                        <a
                            :href="`/back-office/planning?modaladd=1&fillclient=${userId}`"
                            target="_blank"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('datatable_customer_booking_v2_edit.actions.schedule_reservation.label')
                            }}</span>
                        </a>
                        <a
                            :href="`/back-office/planning?modaladd=1&booking=${item.id}`"
                            target="_blank"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('datatable_customer_booking_v2_edit.actions.reschedule_reservations.label')
                            }}</span>
                        </a>

                        <a
                            :href="`/back-office/customer/send_mail_rappel/${userId}/${item.id}`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Mail :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('datatable_customer_booking_v2_edit.actions.send_email.label')
                            }}</span>
                        </a>
                    </div>
                </div>
                <div v-else class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier (Link to V2 Edit Page) -->
                        <a
                            :href="`/back-office/invoice/${item.id}`"
                            target="_blank"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('datatable_customer_products_purchased_v2_edit.actions.btn_receipt.label')
                            }}</span>
                        </a>
                    </div>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
import { eventBus } from '../../../Customer/eventBus'
import DataTable from '../../../Shared/DataTable.vue'
import Search from '../../Experience/components/Search.vue'
import ExportSelect from '../../../Shared/ExportSelect.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import moment from 'moment'
import { SquarePen, Ban, MoreVertical, Download, XCircle, FilePen, ChevronDown, Mail } from 'lucide-vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'OrderTable',
    components: {
        Mail,
        SquarePen,
        Ban,
        MoreVertical,
        Download,
        XCircle,
        FilePen,
        ChevronDown,
        DataTable,
        Search,
        ExportSelect,
    },
    props: {
        userId: {
            type: [Number, String],
            default: null,
        },
        exportView: {
            type: Boolean,
            default: true,
        },
        searchView: {
            type: Boolean,
            default: true,
        },
        isDeleted: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('customer_orders_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            exportLoading: false,
            selectedItems: [],
            sortColumn: 'created_at',
            sortDirection: 'desc',
            // --- Columns ---
            selectedColumnKeys: ['id', 'start', 'prestations', 'nbre', 'status', 'actions'],
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
        }
    },
    computed: {
        allColumns() {
            return [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'start',
                    label: 'Date',
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'prestations',
                    label: 'Réservation',
                    sortable: false,
                    type: 'custom',
                },
                {
                    key: 'nbre',
                    label: 'Nbre',
                    sortable: true,
                    type: 'integer',
                },
                {
                    key: 'status',
                    label: this.$t('invoices.columns.status'),
                    sortable: true,
                    type: 'badge',
                },
                { key: 'actions', label: this.$t('common.actions'), sortable: false, type: 'actions' },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            let cols = this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
            if (this.isDeleted) {
                cols = cols.filter((c) => c.key !== 'actions')
            }
            return cols
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatCurrency(amount) {
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount)
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            const mDate = moment(dateString)
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return mDate.toDate().toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        getStatus(item) {
            const baseClasses =
                'ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs poppinsMedium'

            const statusClasses = {
                upcoming: 'ppv2-bg-blue-100 ppv2-text-blue-800',
                partially_paid: 'ppv2-bg-yellow-100 ppv2-text-yellow-800',
                paid: 'ppv2-bg-green-100 ppv2-text-green-800',
                cancelled: 'ppv2-bg-red-100 ppv2-text-red-800',
            }

            return `<span class="${baseClasses} ${statusClasses[item.status.key] || 'ppv2-bg-gray-100 ppv2-text-gray-800'}">${
                item.status.label
            }</span>`
        },

        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('customer_orders_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            this.fetchData()
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            this.fetchData()
        },
        getParams() {
            const params = {
                search: this.search,
                page: this.currentPage,
                perPage: this.itemsPerPage,
                sortBy: this.sortColumn,
                sortDirection: this.sortDirection,
            }
            return params
        },
        async fetchData() {
            this.loading = true
            try {
                const params = this.getParams()
                const response = await axios.get(`/api/v3/customer/${this.userId}/orders`, {
                    params,
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const data = response.data

                this.recordsTotal = data.meta ? data.meta.total : data.total
                this.tableData = data.data || []
                this.$emit('total-updated', data.activeCount)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async downloadOrders(format = 'csv') {
            this.exportLoading = true
            try {
                const params = this.getParams()
                params.format = format
                const response = await axios.get(`/api/v3/customer/${this.userId}/orders`, {
                    params: { ...params, export: true },
                    responseType: 'blob',
                })

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute(
                    'download',
                    `customer_orders-${this.userId}-${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`
                )
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
                this.$toast?.success?.(this.$t('common.exportSuccess'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.exportLoading = false
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 is min-width of dropdown
            }
        },
    },
    mounted() {
        this.fetchData()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    watch: {
        userId() {
            this.currentPage = 1
            this.fetchData()
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}
.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}
</style>
